/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Typography,
  Grid,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import { getKeywordSearchLogs } from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloseIcon from '@mui/icons-material/Close'
import VisibilityIcon from '@mui/icons-material/Visibility'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import AuditKeywordsFilters from './AuditKeywordsFilters'

interface KeywordSearchLog {
  readonly id?: number
  readonly user_name?: string
  readonly keyword?: string
  readonly station?: string
  readonly search_info?: string
  readonly start_date?: string
  readonly finish_date?: string
  readonly created_at?: string
  readonly search_type?: string
}

interface KeywordSearchLogRow extends KeywordSearchLog {
  readonly 'user:representation_name': string
  readonly location_display: string
  readonly search_start_date: string
  readonly search_finish_date: string
}

interface SearchLogResponse {
  readonly data: readonly KeywordSearchLog[]
  readonly rowsCount: number
}

export default function ListAllAuditKeywords() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const searchType = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const limit = 15
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandUserActions = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] = useState<KeywordSearchLogRow | null>(
    null
  )
  const handleView = (row: KeywordSearchLogRow) => {
    setSelectedRow(row)
    setOpenModal(true)
  }

  const columns: readonly GridColDef[] = [
    { field: 'id' },
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'location_display',
      headerName: translate('Consulted origin'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'keyword',
      headerName: translate('Keywords'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },

    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip title={translate('View')}>
            <IconButton
              onClick={() => handleView(params.row)}
              size="small"
              aria-label={translate('View')}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    searchType.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandUserActions.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!startDate || !finishDate) {
      notify({
        type: 'error',
        message: translate('Start date and end date must be provided'),
      })
      return false
    }

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    const searchLogData: SearchLogResponse = await getKeywordSearchLogs({
      user_id: userId.get() || undefined,
      search_type: searchType.get() || undefined,
      start_date: startDate,
      finish_date: finishDate,
      page: page,
      limit: pageSize,
      orders:
        orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
    }).finally(() => {
      loadingButton.set(false)
      expandUserActions.set(true)
    })
    setRows(
      searchLogData.data.map((element: KeywordSearchLog, index: number) => {
        const generatedId = element.id ?? page * pageSize + index + 1

        const searchTypeLabelMap: Record<string, string> = {
          occurrence_list: 'Entry - Occurrence List',
          dispatch_list: 'Dispatch - Dispatch List',
          video_surveillance: 'Video Surveillance',
          statistics_dispatch: 'Dispatch Statistics',
          search_multiple_coments_report: 'Search Report by Multiple Comments',
          word_cloud: 'Word Cloud',
          connection_logs_report: 'Connection Logs Reports',
        }

        const locationDisplay = element.search_type
          ? translate(
              searchTypeLabelMap[element.search_type] ?? element.search_type
            )
          : element.station ?? ''

        const extractKeywords = (searchInfo: string) => {
          try {
            const info =
              typeof searchInfo === 'string'
                ? JSON.parse(searchInfo)
                : searchInfo
            const keywords = Array.isArray(info?.key_words)
              ? info.key_words
              : []
            return keywords.join('; ')
          } catch {
            return ''
          }
        }

        const extractSearchDate = (
          searchInfo: string,
          key: 'start_date' | 'finish_date'
        ) => {
          try {
            const info =
              typeof searchInfo === 'string'
                ? JSON.parse(searchInfo)
                : searchInfo
            const value = info?.[key]
            if (!value) return ''
            const d = dayjs(value)
            return d.isValid() ? d.format('DD/MM/YYYY HH:mm') : String(value)
          } catch {
            return ''
          }
        }

        return {
          id: generatedId,
          ...element,
          'user:representation_name': element?.user_name || '',
          location_display: locationDisplay,
          keyword: extractKeywords(element.search_info as string),
          search_start_date: extractSearchDate(
            element.search_info as string,
            'start_date'
          ),
          search_finish_date: extractSearchDate(
            element.search_info as string,
            'finish_date'
          ),
        }
      })
    )

    setRowCount(searchLogData.rowsCount)
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditKeywordsFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          searchType={searchType}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() => expandUserActions.set(!expandUserActions.get())}
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandUserActions.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandUserActions.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <Dialog
        fullWidth
        maxWidth={'md'}
        open={openModal}
        onClose={clickOutModalClose}
        disableEscapeKeyDown={true}
      >
        <DialogTitle
          display={'flex'}
          alignItems={'center'}
          justifyContent={'space-between'}
        >
          {translate('Actions Performed')}
          <IconButton aria-label="close" onClick={() => setOpenModal(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ paddingTop: 5 }}>
          {selectedRow && (
            <Grid container xs={12} display={'flex'} flexDirection={'row'}>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('User')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow['user:representation_name']}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Consulted origin')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow.location_display}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Keywords')}:
                </Typography>
                <Typography variant="subtitle2">
                  {selectedRow.keyword}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Period of search')}:
                </Typography>
                <Typography variant="subtitle2">
                  {(() => {
                    const start = selectedRow.search_start_date || ''
                    const finish = selectedRow.search_finish_date || ''
                    if (!start && !finish) return '-'
                    if (start && finish) return `${start} — ${finish}`
                    return start || finish
                  })()}
                </Typography>
              </Grid>
              <Grid item xs={3} sx={{ pr: 1, pt: 2 }}>
                <Typography variant="subtitle1" fontWeight={'bold'}>
                  {translate('Performed at')}:
                </Typography>
                <Typography variant="subtitle2">
                  {dayjs(selectedRow.created_at).format('DD/MM/YYYY HH:mm')}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
